import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import GatewatActionCardItem from './gateway-action-card-item';
import { useGateway } from 'hooks';
import { statusColors } from 'utils/utils';
import GatewayChart from './chart/gateway-chart';
import PermissionBlur from 'features/gateways/components/permission-blur';
import useGatewayAccess from 'features/gateways/hooks/use-gateway-access';

interface CardProps {
    icon: JSX.Element;
    color: string;
    background?: string;
    title: string;
    amount: number;
    count: number;
    periodHint: string;
}

const useFinanceCards = (): CardProps[] => {
    const gateway = useGateway();
    const statics = gateway?.statics;

    return [
        {
            color: statusColors.success.color,
            background: statusColors.success.background,
            icon: <i className="fa-light fa-sun-bright" />,
            title: 'درآمد امروز',
            amount: statics?.today_amount || 0,
            count: statics?.today_count || 0,
            periodHint: 'از ابتدای امروز تا این لحظه',
        },
        {
            color: statusColors.search.color,
            background: statusColors.search.background,
            icon: <i className="fa-light fa-clock-rotate-left" />,
            title: 'درآمد دیروز',
            amount: statics?.yesterday_amount || 0,
            count: statics?.yesterday_count || 0,
            periodHint: 'عملکرد کامل روز گذشته',
        },
        {
            color: statusColors.warning.color,
            background: statusColors.warning.background,
            icon: <i className="fa-light fa-calendar-week" />,
            title: 'درآمد هفته',
            amount: statics?.week_amount || 0,
            count: statics?.week_count || 0,
            periodHint: 'مجموع ۷ روز اخیر',
        },
        {
            color: statusColors.search.color,
            background: statusColors.search.background,
            icon: <i className="fa-light fa-calendar-days" />,
            title: 'درآمد ماه',
            amount: statics?.month_amount || 0,
            count: statics?.month_count || 0,
            periodHint: 'از ابتدای ماه جاری',
        },
    ];
};

const OverallFinanceMetrics: React.FC = () => {
    const cards = useFinanceCards();
    const { canViewTransactions } = useGatewayAccess();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: { xs: 1.15, md: 1.4 },
            }}>
            {cards.map((card) => (
                <PermissionBlur
                    key={card.title}
                    allowed={canViewTransactions}
                    label="دسترسی تراکنش‌ها برای شما فعال نیست"
                    borderRadius={3.5}>
                    <GatewatActionCardItem {...card} />
                </PermissionBlur>
            ))}
        </Box>
    );
};

const OverallFinanceChart: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    return (
        <Card
            elevation={0}
            sx={(theme) => ({
                height: '100%',
                minHeight: 410,
                borderRadius: '18px',
                border: 0,
                overflow: 'hidden',
                background: `linear-gradient(155deg, ${alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.03 : 0.018,
                )} 0%, transparent 42%), ${theme.palette.background.paper}`,
                boxShadow:
                    theme.palette.mode === 'light'
                        ? '0 12px 36px rgba(17, 24, 39, 0.045)'
                        : `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`,
            })}>
            <Box
                sx={{
                    px: { xs: 1.75, md: 2.15 },
                    pt: 1.75,
                    pb: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        className="font-yekan-heavy"
                        sx={{ fontSize: 14.5, color: 'text.primary' }}>
                        روند عملکرد درگاه
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.2,
                            fontSize: 10.5,
                            lineHeight: 1.65,
                            color: 'text.secondary',
                        }}>
                        مقایسه درآمد و تعداد تراکنش‌های موفق در بازه‌های مختلف
                    </Typography>
                </Box>
                <Box
                    sx={(theme) => ({
                        width: 38,
                        height: 38,
                        borderRadius: '12px',
                        bgcolor: alpha(
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.13 : 0.08,
                        ),
                        color:
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& i': { fontSize: 15 },
                    })}>
                    <i className="fa-light fa-chart-mixed" />
                </Box>
            </Box>
            <CardContent
                sx={{
                    p: { xs: 1.05, md: 1.35 },
                    pt: { xs: 0.5, md: 0.75 },
                    '&:last-child': { pb: 1.35 },
                }}>
                <GatewayChart disabled={disabled} height={292} />
            </CardContent>
        </Card>
    );
};

export { OverallFinanceMetrics, OverallFinanceChart };
